import React, { useState } from 'react';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';

import { ProductItemProps } from '../../../../models/Product';
import { useScheduleContext } from '../../../../contexts/ScheduleContext';
import { Payment, ScheduleItemPropsBuilder } from '../../../../models/Schedule';
import { PatientProps, usePatientContext } from '../../../../contexts/PatientContext';
import { fetchRuleValidation } from '../../../../modules/validateRules';
import { makePatientRuleByPatientProps } from '../../../../models/Patient';
import { LoadingOverlay } from '../../../../components';
import { isAxiosError } from '../../../../utils/errors';
import { useRouter } from '../../../../contexts/RouterContext';
import { useProducts } from '../../store/Products';
import { ResourceSelectorTemplate } from '../../../../templates/ResourceSelectorTemplate';
import { Resources } from '../../../../models/ScheduleResource';
import { fetchProductById } from '../../../../modules/products';
import { PAGES } from '../../../../Router/pages';
import { useModuleContext } from '../../../../contexts/ModuleContext';
import { useCartContext } from '../../../../contexts/CartContext';
import { RuleValidationResponseData } from '../../../../models/RuleValidator';
import { useGenericModal } from '../../../../hooks/useGenericModal';
import { useEligibility } from '../../hooks/useEligibility';
import { PAYMENT_TYPES, PaymentValidation, PaymentValidationRule } from '../../../../models/PaymentRule';
import { useModalsContext } from '../../../../contexts/ModalsContext';
import { FirstAppointmentModal } from '../../components/FirstAppointmentModal';
import { DoctorFirstStepModal } from '../../components/DoctorFirstStepModal';
import { removerAccents } from '../../../../utils/masks';
import { productComparetor } from './productComparetor';

export function ProductList() {
  const { patientSelected: patient } = usePatientContext();
  const [loading, setLoading] = React.useState(false);
  const [isFirstAppointmentModalVisible, setIsFirstAppointmentModalVisible] = React.useState(false);
  const [isDoctorFirstStepModalVisible, setIsDoctorFirstStepModalVisible] = React.useState(false);
  const [preventUser, setPreventUser] = React.useState(false);
  const [selectedResource, setSelectedResource] = React.useState<Resources>();
  const [isEngravidaProgram, setIsEngravidaProgram] = useState(false);

  const { goBack, goTo } = useRouter();

  const { changeSchedule, selectedItem: schedule } = useScheduleContext();

  const { products } = useProducts();

  const { selectItemToSchedule } = useModuleContext();

  const { addProductList } = useCartContext();

  const genericModal = useGenericModal({
    goTo,
    schedule
  });

  const { openModal } = useModalsContext();

  const { validateEligibilityPlans } = useEligibility();

  function handleProductIcons(products: Resources[]) {
    return products.map(item => {
      if (item.name.includes('Médico'))
        return {
          ...item,
          icon: 'STETHOSCOPE.svg'
        };
      if (item.name.includes('Enfermeiro'))
        return {
          ...item,
          icon: 'MEDICAL_KIT.svg'
        };
      return item;
    });
  }

  const sanitizedProducts = React.useMemo(() => {
    const productsWithIcon = handleProductIcons(products);
    return productsWithIcon.sort(productComparetor);
  }, [products]);

  function convertToPaymentRule(payment: Payment) {
    if (payment.type === PAYMENT_TYPES.B2B && payment.eligibility?.codPlan)
      return PaymentValidation({
        type: PAYMENT_TYPES.B2B,
        additional_info: {
          plan_code: payment.eligibility?.codPlan
        }
      });
    if (payment.type === PAYMENT_TYPES.PARTICULAR)
      return PaymentValidation({
        type: PAYMENT_TYPES.PARTICULAR
      });
    return {} as PaymentValidationRule;
  }

  async function validateRules(props: {
    product: ProductItemProps;
    patient: PatientProps;
    payment: Payment;
  }): Promise<RuleValidationResponseData | undefined> {
    setPreventUser(false);

    try {
      const response = await fetchRuleValidation({
        product: props.product,
        patient: makePatientRuleByPatientProps(props.patient),
        payment: convertToPaymentRule(props.payment)
      });

      if (response.error && response.errorStatus) {
        openModal('SERVICE_UNAVAILABLE');
        return;
      }

      const validated = response.data?.data;

      if (!validated.product?.coverages?.length) openModal('PRODUCT_NOT_COVERED');
      else return response.data?.data;
    } catch (e) {
      const error = e as AxiosError;

      console.error('Error trying validate service', error);
      openModal('SERVICE_UNAVAILABLE');
    }
  }

  async function getCatalogProduct(productId: string) {
    try {
      const result = await fetchProductById(productId);
      return result.data;
    } catch (error) {
      toast.error('Erro ao obter produtos.');
    }
  }

  async function createSchedule(product: Resources) {
    try {
      setLoading(true);
      selectItemToSchedule('PRODUCT');

      const catalogProduct = await getCatalogProduct(product.id);

      if (patient && catalogProduct) {
        const payments = await validateEligibilityPlans({ productId: catalogProduct.id, patient });

        if (!payments?.length) openModal('NO_ELIGIBILITY');
        if (payments && payments?.length > 1) return goTo(PAGES.PAYMENT_VIEW.id);

        if (payments?.length === 1) {
          const [payment] = payments;

          const scheduleFactory = ScheduleItemPropsBuilder();

          scheduleFactory.setPayment(payment);
          scheduleFactory.setPatient(patient?.idPatient ?? '');

          const IS_PREGNANT = !!patient?.pregnant;
          const IS_EMPLOYEE = payment?.eligibility?.plan.toUpperCase().includes('ALBERT EINSTEIN');

          setIsEngravidaProgram(IS_PREGNANT && !!IS_EMPLOYEE);

          const validated = await validateRules({ product: catalogProduct, patient, payment });
          const productValidated = validated?.product;

          if (productValidated) {
            addProductList(productValidated);

            scheduleFactory.setProduct({ ...productValidated, name: productValidated.name });
            genericModal.addGenericModals({
              productRules: validated.product.rules,
              additional: validated.additional_information
            });

            if (!validated.resource) {
              changeSchedule({ ...schedule, ...scheduleFactory.build() });
              setIsFirstAppointmentModalVisible(true);
            } else {
              scheduleFactory.setResource(true, validated.resource);

              changeSchedule({ ...schedule, ...scheduleFactory.build() });

              if (IS_PREGNANT && IS_EMPLOYEE) {
                setLoading(false);
                return goTo(PAGES.PREGNANT_INFO.id);
              }

              goTo(PAGES.SERVICE_TYPE_PAGE.id);
            }
          }
        }
      }
      setIsDoctorFirstStepModalVisible(false);
      setLoading(false);
    } catch (e) {
      console.error('🚀 ~ createSchedule ~ e:', e);

      openModal('SERVICE_UNAVAILABLE');
    }
  }

  function onSubmit(product: Resources) {
    if (removerAccents(product.name).toLocaleLowerCase().includes('medico')) setIsDoctorFirstStepModalVisible(true);
    else createSchedule(product);
  }

  return (
    <>
      <ResourceSelectorTemplate
        headTitle="Qual produto deseja agendar?"
        resources={sanitizedProducts}
        handleSelectProduct={setSelectedResource}
        handleResources={product => onSubmit(product)}
        goBack={goBack}
        buttonDisabled={preventUser}
      />
      <FirstAppointmentModal
        visible={isFirstAppointmentModalVisible}
        onClose={() => setIsFirstAppointmentModalVisible(false)}
        handleContinue={() => goTo(isEngravidaProgram ? PAGES.PREGNANT_INFO.id : PAGES.SERVICE_TYPE_PAGE.id)}
      />

      <DoctorFirstStepModal
        visible={isDoctorFirstStepModalVisible}
        onClose={() => setIsDoctorFirstStepModalVisible(false)}
        scheduleWithDoctor={() => selectedResource && createSchedule(selectedResource)}
        scheduleWithNurse={() => {
          const nurseProduct = products.find(product => product.name.toLocaleLowerCase().includes('enfermeiro'));
          nurseProduct && createSchedule(nurseProduct);
        }}
      />
      <LoadingOverlay loading={loading} />
    </>
  );
}
